import { isFavorite, toggleFavorite, onFavoritesChange } from '@theme/favorites-store';

/**
 * <s-fav-button> toggles one product in the favorites list.
 *
 * Expects a child <button>. Reads data-handle, data-title, data-url, data-image,
 * data-label-add, data-label-remove, data-added-message and data-removed-message.
 * aria-pressed drives the filled heart in CSS; data-pop plays the short pop animation.
 * The button never touches the cart.
 */
class SFavButton extends HTMLElement {
  #ac = null;
  #popTimer = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const button = this.querySelector('button');
    if (!button || !this.dataset.handle) return;

    button.addEventListener(
      'click',
      (event) => {
        // Cards stretch a link underneath; the heart must not navigate.
        event.preventDefault();
        event.stopPropagation();
        this.#toggle();
      },
      { signal: this.#ac.signal },
    );

    onFavoritesChange(() => this.#render(), this.#ac.signal);
    this.#render();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearTimeout(this.#popTimer);
  }

  #toggle() {
    const added = toggleFavorite({
      handle: this.dataset.handle,
      title: this.dataset.title,
      url: this.dataset.url,
      image: this.dataset.image,
    });

    this.removeAttribute('data-pop');
    // Force a reflow so the animation restarts on quick repeated clicks.
    void this.offsetWidth;
    this.setAttribute('data-pop', '');
    clearTimeout(this.#popTimer);
    this.#popTimer = setTimeout(() => this.removeAttribute('data-pop'), 450);

    const message = added ? this.dataset.addedMessage : this.dataset.removedMessage;
    if (message) {
      document.dispatchEvent(
        new CustomEvent('s-notification:add', { detail: { message, variant: 'info', duration: 2500 } }),
      );
    }
  }

  #render() {
    const button = this.querySelector('button');
    if (!button) return;
    const active = isFavorite(this.dataset.handle);
    const label = active ? this.dataset.labelRemove : this.dataset.labelAdd;
    button.setAttribute('aria-pressed', active ? 'true' : 'false');
    if (label) {
      button.setAttribute('aria-label', label);
      button.title = label;
    }
    const text = this.querySelector('[data-fav-text]');
    if (text && label) text.textContent = label;
  }
}

if (!customElements.get('s-fav-button')) customElements.define('s-fav-button', SFavButton);
